import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Filter, Check, X } from "lucide-react";

export function FilterDropdown({
  columnKey,
  options,
  selectedValues,
  onToggle,
  onSelectAll,
  onClear,
  isActive,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState(null);

  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const PANEL_WIDTH = 220;
  const PANEL_MAX_HEIGHT = 320;
  const MARGIN = 8;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpwards = spaceBelow < PANEL_MAX_HEIGHT + MARGIN && rect.top > spaceBelow;

    let left = rect.left;
    if (left + PANEL_WIDTH > window.innerWidth - MARGIN) {
      left = Math.max(MARGIN, window.innerWidth - PANEL_WIDTH - MARGIN);
    }

    setPosition({
      left,
      top: openUpwards ? undefined : rect.bottom + 4,
      bottom: openUpwards ? window.innerHeight - rect.top + 4 : undefined,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };

    const handleReposition = () => updatePosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  const filtered = options.filter((o) => {
    const label = typeof o === "string" ? o : o.label;
    return label.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`p-1 rounded-md transition-colors ${isActive
          ? "text-primary bg-accent"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        title="Filtrar"
      >
        <Filter size={13} />
      </button>

      {open && position && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[1999] surface-panel shadow-pop flex flex-col animate-in fade-in zoom-in-95 duration-150"
          style={{
            left: position.left,
            top: position.top,
            bottom: position.bottom,
            width: PANEL_WIDTH,
            maxHeight: PANEL_MAX_HEIGHT,
          }}
        >
          <div className="p-2 border-b border-border">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background text-foreground outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary"
              autoFocus
            />
          </div>

          <div className="flex gap-1 px-2 pt-2 pb-1">
            <button
              onClick={() => onSelectAll(columnKey, filtered)}
              className="text-xs px-2 py-1 rounded-md bg-muted text-secondary-foreground hover:bg-accent transition-colors"
            >
              Selecionar tudo
            </button>

            <button
              onClick={() => onClear(columnKey)}
              className="text-xs px-2 py-1 rounded-md bg-muted text-secondary-foreground hover:bg-accent transition-colors flex items-center gap-0.5"
            >
              <X size={10} /> Limpar
            </button>
          </div>

          <div className="overflow-y-auto flex-1 p-1">
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground p-2 text-center">
                Nenhum resultado
              </div>
            )}
            {filtered.map((opt) => {
              const value = typeof opt === "string" ? opt : opt.value;
              const label = typeof opt === "string" ? opt : opt.label;

              const checked = selectedValues.has(value);

              return (
                <label
                  key={value}
                  onClick={() => onToggle(columnKey, value)}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer rounded-md hover:bg-muted text-foreground"
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${checked
                        ? "bg-table-filter-active border-table-filter-active text-white"
                        : "border-input bg-background"
                      }`}
                  >
                    {checked && <Check size={12} />}
                  </span>

                  <span className="truncate text-xs">{label}</span>
                </label>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
